import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { db } from '../../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar, 
  Clock, 
  Users, 
  Phone, 
  User, 
  CheckCircle, 
  XCircle, 
  Trash2,
  MapPin,
  RefreshCw,
  Filter
} from 'lucide-react';

export default function AdminBranchReservations() {
  const { branchId } = useParams();
  const { user, isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed, cancelled
  const [dateFilter, setDateFilter] = useState('upcoming'); // upcoming, today, all

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) {
      navigate('/login');
    }
  }, [user, authLoading, isAdmin, navigate]);

  useEffect(() => {
    if (branchId) {
      fetchBranch();
      // Set up real-time listener for reservations
      const unsubscribe = setupReservationsListener();
      return () => unsubscribe();
    }
  }, [branchId]);

  const fetchBranch = async () => {
    try {
      const docRef = doc(db, 'branches', branchId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setBranch({ id: docSnap.id, ...docSnap.data() });
      }
    } catch (error) {
      console.error('Error fetching branch:', error);
    }
  };

  const setupReservationsListener = () => {
    // Simple query without orderBy to avoid index issues
    const q = query(
      collection(db, 'reservations'),
      where('branchId', '==', branchId)
    );

    return onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        // Sort in client-side to avoid Firestore composite index requirement
        const sortedData = data.sort((a, b) => {
          const dateA = new Date(a.createdAt || a.date);
          const dateB = new Date(b.createdAt || b.date);
          return dateB - dateA;
        });
        setReservations(sortedData);
        setLoading(false);
        console.log('✅ Rezervasiyalar real-time yeniləndi:', sortedData.length);
      },
      (error) => {
        console.error('❌ Error listening to reservations:', error);
        console.error('Error code:', error.code);
        console.error('Error message:', error.message);
        
        if (error.code === 'failed-precondition') {
          console.error('⚠️ Firestore index lazımdır. Browser console-da link olmalıdır.');
        }
        
        setLoading(false);
        toast.error('Rezervasiyalar yüklənərkən xəta baş verdi');
      }
    );
  };

  const handleConfirm = async (reservationId) => {
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        status: 'confirmed',
        updatedAt: new Date().toISOString()
      });
      toast.success('Rezervasiya təsdiqləndi');
    } catch (error) {
      console.error('Error confirming reservation:', error);
      toast.error('Xəta baş verdi');
    }
  };

  const handleCancel = async (reservationId) => {
    if (!window.confirm('Bu rezervasiyanı ləğv etmək istəyirsiniz?')) return;
    
    try {
      await updateDoc(doc(db, 'reservations', reservationId), {
        status: 'cancelled',
        updatedAt: new Date().toISOString()
      });
      toast.success('Rezervasiya ləğv edildi');
    } catch (error) {
      console.error('Error cancelling reservation:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        toast.error('Ləğv etmə icazəsi yoxdur. Firebase rules-u yoxlayın.');
      } else {
        toast.error(`Xəta: ${error.message}`);
      }
    }
  };

  const handleDelete = async (reservationId) => {
    if (!window.confirm('Bu rezervasiyanı silmək istəyirsiniz? Bu əməliyyat geri qaytarıla bilməz.')) return;
    
    try {
      await deleteDoc(doc(db, 'reservations', reservationId));
      toast.success('Rezervasiya silindi');
    } catch (error) {
      console.error('Error deleting reservation:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      
      if (error.code === 'permission-denied') {
        toast.error('Silmə icazəsi yoxdur. Firebase rules-u yoxlayın.');
      } else {
        toast.error(`Xəta: ${error.message}`);
      }
    }
  };

  // Filter reservations
  const filteredReservations = reservations.filter(res => {
    // Status filter
    if (filter !== 'all' && res.status !== filter) return false;
    
    // Date filter
    const today = new Date().toISOString().split('T')[0];
    if (dateFilter === 'today' && res.date !== today) return false;
    if (dateFilter === 'upcoming' && res.date < today) return false;
    
    return true;
  });

  // Sort by date and time
  const sortedReservations = [...filteredReservations].sort((a, b) => {
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.time.localeCompare(b.time);
  });

  // Count by status
  const pendingCount = reservations.filter(r => r.status === 'pending').length;
  const confirmedCount = reservations.filter(r => r.status === 'confirmed').length;

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Təsdiqlənib</span>;
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-medium flex items-center gap-1"><Clock className="w-3 h-3" /> Gözləyir</span>;
      case 'cancelled':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium flex items-center gap-1"><XCircle className="w-3 h-3" /> Ləğv</span>;
      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050000] pt-20 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050000] pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/reservations')}
              className="text-gray-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                <MapPin className="w-6 h-6 text-red-500" />
                {branch?.name || 'Filial'} - Rezervasiyalar
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                {pendingCount > 0 && <span className="text-yellow-400">{pendingCount} gözləyən</span>}
                {pendingCount > 0 && confirmedCount > 0 && ' • '}
                {confirmedCount > 0 && <span className="text-green-400">{confirmedCount} təsdiqlənmiş</span>}
              </p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 mb-6">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
              className={`text-xs sm:text-sm ${filter === 'all' ? 'bg-red-500' : 'border-white/20 text-gray-400'}`}
            >
              Hamısı
            </Button>
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('pending')}
              className={`text-xs sm:text-sm ${filter === 'pending' ? 'bg-yellow-500' : 'border-white/20 text-gray-400'}`}
            >
              Gözləyən
            </Button>
            <Button
              variant={filter === 'confirmed' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('confirmed')}
              className={`text-xs sm:text-sm ${filter === 'confirmed' ? 'bg-green-500' : 'border-white/20 text-gray-400'}`}
            >
              Təsdiqlənmiş
            </Button>
            <Button
              variant={filter === 'cancelled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('cancelled')}
              className={`text-xs sm:text-sm ${filter === 'cancelled' ? 'bg-red-500' : 'border-white/20 text-gray-400'}`}
            >
              Ləğv
            </Button>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button
              variant={dateFilter === 'upcoming' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('upcoming')}
              className={`text-xs sm:text-sm ${dateFilter === 'upcoming' ? 'bg-blue-500' : 'border-white/20 text-gray-400'}`}
            >
              Qarşıdakı
            </Button>
            <Button
              variant={dateFilter === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('today')}
              className={`text-xs sm:text-sm ${dateFilter === 'today' ? 'bg-blue-500' : 'border-white/20 text-gray-400'}`}
            >
              Bu gün
            </Button>
            <Button
              variant={dateFilter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDateFilter('all')}
              className={`text-xs sm:text-sm ${dateFilter === 'all' ? 'bg-blue-500' : 'border-white/20 text-gray-400'}`}
            >
              Hamısı
            </Button>
          </div>
        </div>

        {/* Reservations List */}
        {sortedReservations.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">Rezervasiya yoxdur</h3>
            <p className="text-gray-400">Bu filtrlə uyğun rezervasiya tapılmadı</p>
          </div>
        ) : (
          <div className="space-y-4">
            {sortedReservations.map((reservation, index) => (
              <motion.div
                key={reservation.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`bg-[#0F0F0F] border rounded-lg p-4 md:p-6 ${
                  reservation.status === 'pending' 
                    ? 'border-yellow-500/30' 
                    : reservation.status === 'confirmed'
                    ? 'border-green-500/30'
                    : 'border-white/10'
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  {/* Left - Date & Time */}
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="text-center bg-white/5 rounded-lg p-2 sm:p-3 min-w-[60px] sm:min-w-[70px]">
                      <div className="text-xl sm:text-2xl font-bold text-red-500">
                        {reservation.date.split('-')[2]}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(reservation.date).toLocaleDateString('az-AZ', { month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-white font-medium text-sm sm:text-base">
                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500" />
                        {reservation.time}
                      </div>
                      <div className="text-gray-400 text-xs sm:text-sm mt-1">{reservation.gameName}</div>
                      {getStatusBadge(reservation.status)}
                    </div>
                  </div>

                  {/* Middle - Customer Info */}
                  <div className="flex-1 grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <div className="text-gray-500 text-xs flex items-center gap-1">
                        <User className="w-3 h-3" /> Ad Soyad
                      </div>
                      <div className="text-white font-medium text-sm sm:text-base break-words">{reservation.customerName}</div>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Telefon
                      </div>
                      <a href={`tel:${reservation.customerPhone}`} className="text-white font-medium text-sm sm:text-base hover:text-red-400 break-all">
                        {reservation.customerPhone}
                      </a>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs flex items-center gap-1">
                        <Users className="w-3 h-3" /> Nəfər
                      </div>
                      <div className="text-white font-medium text-sm sm:text-base">{reservation.playerCount} nəfər</div>
                    </div>
                  </div>

                  {/* Right - Actions */}
                  <div className="flex flex-wrap gap-2 justify-end sm:justify-start">
                    {reservation.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          className="bg-green-500 hover:bg-green-600 text-xs sm:text-sm"
                          onClick={() => handleConfirm(reservation.id)}
                        >
                          <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Təsdiqlə
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500 text-red-500 hover:bg-red-500/10 text-xs sm:text-sm"
                          onClick={() => handleCancel(reservation.id)}
                        >
                          <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          Ləğv Et
                        </Button>
                      </>
                    )}
                    {reservation.status === 'confirmed' && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500/10 text-xs sm:text-sm"
                        onClick={() => handleCancel(reservation.id)}
                      >
                        <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        Ləğv Et
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-400 hover:text-red-400"
                      onClick={() => handleDelete(reservation.id)}
                      title="Sil"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
